import { useState } from "react";
import {
  Users,
  Trophy,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  Star,
  MessageSquare,
  Award,
  Medal,
  ChevronUp,
  ChevronDown,
  Minus,
  Crown,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  teamMembers,
  leaderboard,
  teamWeeklyTrend,
  teamSummary,
} from "../data/teamMetrics";
import { exportCSV, exportJSON, exportPDF } from "../utils/exportData";

import { useTranslation } from 'react-i18next';
// ─── Rank Badge ───
function RankBadge({ rank }) {
  if (rank === 1) return <Crown className="w-4 h-4 text-amber-500" />;
  if (rank === 2) return <Medal className="w-4 h-4 text-zinc-400" />;
  if (rank === 3) return <Medal className="w-4 h-4 text-amber-700" />;
  return <span className="font-mono text-xs text-zinc-500">#{rank}</span>;
}

// ─── Trend Icon ───
function TrendIcon({ trend, value }) {
  if (trend === "up") return <ChevronUp className="w-3.5 h-3.5 text-emerald-400" />;
  if (trend === "down") return <ChevronDown className="w-3.5 h-3.5 text-red-400" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
}

// ─── Member Card ───
function MemberCard({ member }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-zinc-800 bg-zinc-950/30 hover:border-zinc-700 transition-colors">
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center text-sm font-display font-bold uppercase"
            style={{ backgroundColor: member.color + "20", borderColor: member.color + "50", borderWidth: 1, color: member.color }}
          >
            {member.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display text-sm text-white uppercase truncate">{member.name}</span>
              <TrendIcon trend={member.trend} value={member.trendValue} />
              <span className={`font-mono text-[10px] ${member.trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
                {member.trend === "up" ? "+" : ""}{member.trendValue}%
              </span>
            </div>
            <span className="font-mono text-[10px] text-zinc-500 uppercase">{member.role}</span>
          </div>
          <div className="text-right">
            <div className="font-display text-lg text-white">{member.tasksCompleted}</div>
            <div className="font-mono text-[9px] text-zinc-500 uppercase">tasks</div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-zinc-800 pt-3">
          <div>
            <div className="font-mono text-[9px] text-zinc-500 uppercase mb-1">Avg Response</div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-amber-500" />
              <span className="font-mono text-xs text-white">{member.responseTime}s</span>
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] text-zinc-500 uppercase mb-1">Satisfaction</div>
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500" />
              <span className="font-mono text-xs text-white">{member.satisfactionScore}%</span>
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] text-zinc-500 uppercase mb-1">Resolution</div>
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
              <span className="font-mono text-xs text-white">{member.resolutionRate}%</span>
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] text-zinc-500 uppercase mb-1">Messages</div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3 text-sky-500" />
              <span className="font-mono text-xs text-white">{member.messagesHandled.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Custom Tooltip ───
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 p-3 shadow-xl rounded-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-zinc-300">{entry.name}:</span>
          <span className="font-mono font-bold text-white">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Section Label ───
function SectionLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
      {children}
    </div>
  );
}

// ─── Comparison Bar ───
function ComparisonBar({ members, metric, label, unit = "", colorKey }) {
  const maxVal = Math.max(...members.map((m) => m[metric]));
  const sorted = [...members].sort((a, b) => b[metric] - a[metric]);

  return (
    <div className="space-y-2">
      <SectionLabel>{label}</SectionLabel>
      {sorted.map((m) => (
        <div key={m.id} className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] text-zinc-400 uppercase">{m.name}</span>
            <span className="font-mono text-[10px] text-white font-bold">
              {typeof m[metric] === "number" && m[metric] % 1 !== 0 ? m[metric].toFixed(1) : m[metric]}{unit}
            </span>
          </div>
          <div className="h-2 bg-zinc-900 overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${(m[metric] / maxVal) * 100}%`,
                backgroundColor: m[colorKey] || "#f59e0b",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───
export default function TeamMetrics({ period = "30d", channel = "all" }) {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState("leaderboard"); // 'leaderboard' | 'comparison' | 'trends'

  // Export handlers
  const handleExport = (format) => {
    const data = leaderboard.map((m) => ({
      rank: m.rank,
      name: m.name,
      role: m.role,
      tasks: m.tasksCompleted,
      satisfaction: m.satisfactionScore,
      responseTime: m.responseTime,
      resolution: m.resolutionRate,
      score: m.score,
    }));
    if (format === "csv") exportCSV(data, "team_leaderboard");
    else if (format === "json") exportJSON(data, "team_leaderboard");
    else if (format === "pdf") exportPDF(data, "team_leaderboard", "Team Leaderboard");
  };

  const comparisonData = teamMembers.map((m) => ({
    name: m.avatar,
    tasks: m.tasksCompleted,
    satisfaction: m.satisfactionScore,
    resolution: m.resolutionRate,
  }));

  return (
    <div className="space-y-6">
      {/* KPI Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="border border-zinc-800 bg-zinc-950/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('team_metrics.total_tasks')}</span>
          </div>
          <div className="font-display text-2xl text-white">{teamSummary.totalTasks.toLocaleString()}</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-950/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('team_metrics.avg_satisfaction')}</span>
          </div>
          <div className="font-display text-2xl text-white">{teamSummary.avgSatisfaction}%</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-950/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('team_metrics.avg_response')}</span>
          </div>
          <div className="font-display text-2xl text-white">{teamSummary.avgResponseTime}s</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-950/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-sky-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('team_metrics.avg_resolution')}</span>
          </div>
          <div className="font-display text-2xl text-white">{teamSummary.avgResolution}%</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-950/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-purple-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('team_metrics.total_messages')}</span>
          </div>
          <div className="font-display text-2xl text-white">{teamSummary.totalMessages.toLocaleString()}</div>
        </div>
      </div>

      {/* View Tabs + Export */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex gap-1 border border-zinc-800 p-1">
          {[
            { key: "leaderboard", label: "Leaderboard", icon: Trophy },
            { key: "comparison", label: "Comparison", icon: BarChart },
            { key: "trends", label: "Trends", icon: TrendingUp },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveView(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${
                activeView === key
                  ? "bg-amber-500/10 text-amber-500 border border-amber-500/30"
                  : "text-zinc-500 hover:text-zinc-300 border border-transparent"
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport("csv")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-500 border border-zinc-800 hover:border-amber-500/50 px-2 py-1 transition-colors"
          >
            CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-500 border border-zinc-800 hover:border-amber-500/50 px-2 py-1 transition-colors"
          >
            JSON
          </button>
          <button
            onClick={() => handleExport("pdf")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-500 border border-zinc-800 hover:border-amber-500/50 px-2 py-1 transition-colors"
          >
            PDF
          </button>
        </div>
      </div>

      {/* Leaderboard View */}
      {activeView === "leaderboard" && (
        <div className="border border-zinc-800 bg-zinc-950/30">
          <div className="p-4 border-b border-zinc-800">
            <SectionLabel>// TEAM LEADERBOARD</SectionLabel>
            <h3 className="font-display text-sm uppercase">{t('team_metrics.rankings_by_composite_score')}</h3>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">
              Score = tasks×0.3 + satisfaction×2 + resolution×1.5 - response×10
            </p>
          </div>
          <div className="divide-y divide-zinc-900">
            {leaderboard.map((member) => (
              <div
                key={member.id}
                className={`p-4 flex items-center gap-4 hover:bg-zinc-950/50 transition-colors ${
                  member.rank === 1 ? "bg-amber-500/5" : ""
                }`}
              >
                <div className="w-8 flex items-center justify-center">
                  <RankBadge rank={member.rank} />
                </div>
                <div
                  className="w-10 h-10 flex items-center justify-center text-sm font-display font-bold uppercase shrink-0"
                  style={{ backgroundColor: member.color + "20", borderColor: member.color + "50", borderWidth: 1, color: member.color }}
                >
                  {member.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm text-white uppercase">{member.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase">{member.role}</div>
                </div>
                <div className="hidden md:grid grid-cols-4 gap-6 text-right">
                  <div>
                    <div className="font-display text-sm text-white">{member.tasksCompleted}</div>
                    <div className="font-mono text-[9px] text-zinc-500 uppercase">{t('dashboard.tasks')}</div>
                  </div>
                  <div>
                    <div className="font-display text-sm text-white">{member.responseTime}s</div>
                    <div className="font-mono text-[9px] text-zinc-500 uppercase">Response</div>
                  </div>
                  <div>
                    <div className="font-display text-sm text-white">{member.satisfactionScore}%</div>
                    <div className="font-mono text-[9px] text-zinc-500 uppercase">Satisfaction</div>
                  </div>
                  <div>
                    <div className="font-display text-sm text-emerald-400">{member.resolutionRate}%</div>
                    <div className="font-mono text-[9px] text-zinc-500 uppercase">Resolution</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl text-amber-500">{member.score}</div>
                  <div className="font-mono text-[9px] text-zinc-500 uppercase">Score</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparison View */}
      {activeView === "comparison" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart Comparison */}
            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <SectionLabel>// TASKS COMPARISON</SectionLabel>
              <h3 className="font-display text-sm uppercase mb-4">{t('team_metrics.tasks_completed_per_member')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={comparisonData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#71717a", fontSize: 11, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="tasks" name={t('team_metrics.tasks')} radius={[4, 4, 0, 0]}>
                    {teamMembers.map((m, i) => (
                      <Cell key={`cell-${i}`} fill={m.color} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Satisfaction Chart */}
            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <SectionLabel>// SATISFACTION COMPARISON</SectionLabel>
              <h3 className="font-display text-sm uppercase mb-4">{t('team_metrics.satisfaction_score_per_member')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={comparisonData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#71717a", fontSize: 11, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[80, 100]}
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="satisfaction" name={t('team_metrics.satisfaction')} radius={[4, 4, 0, 0]}>
                    {teamMembers.map((m, i) => (
                      <Cell key={`cell-${i}`} fill={m.color} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <ComparisonBar members={teamMembers} metric="responseTime" label={t('team_metrics.response_time_lower_is_better')} unit="s" colorKey="color" />
            </div>
            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <ComparisonBar members={teamMembers} metric="resolutionRate" label={t('team_metrics.resolution_rate')} unit="%" colorKey="color" />
            </div>
          </div>
        </div>
      )}

      {/* Trends View */}
      {activeView === "trends" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <SectionLabel>// WEEKLY TASKS</SectionLabel>
              <h3 className="font-display text-sm uppercase mb-4">{t('team_metrics.tasks_completed_per_week')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={teamWeeklyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="tasks" name={t('team_metrics.tasks')} fill="#f59e0b" radius={[4, 4, 0, 0]} fillOpacity={0.8} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-zinc-800 bg-zinc-950/30 p-5">
              <SectionLabel>// SATISFACTION + RESOLUTION TREND</SectionLabel>
              <h3 className="font-display text-sm uppercase mb-4">4-Week Trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={teamWeeklyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="week"
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[70, 100]}
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    axisLine={{ stroke: "#27272a" }}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="satisfaction"
                    name={t('team_metrics.satisfaction')}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#f59e0b" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="resolution"
                    name={t('team_metrics.resolution')}
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#10b981" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Response Time Trend */}
          <div className="border border-zinc-800 bg-zinc-950/30 p-5">
            <SectionLabel>// RESPONSE TIME TREND</SectionLabel>
            <h3 className="font-display text-sm uppercase mb-4">Weekly Average Response Time (s)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={teamWeeklyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                  axisLine={{ stroke: "#27272a" }}
                  tickLine={false}
                />
                <YAxis
                  domain={[1.0, 1.6]}
                  tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                  axisLine={{ stroke: "#27272a" }}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="responseTime"
                  name={t('team_metrics.avg_response_s')}
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#0ea5e9" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Member Cards */}
      <div>
        <SectionLabel>// TEAM MEMBERS</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teamMembers.map((member) => (
            <MemberCard key={member.id} member={member} />
          ))}
        </div>
      </div>
    </div>
  );
}
